import { Component, HostBinding, Input } from '@angular/core';
import { Emphasis } from '../emphasis';
import { TranslationRequest } from '../models/TranslationRequest';

@Component({
  selector: 'app-button',
  template: `
    <button
      mat-flat-button
      class="text custom-button"
      [attr.role]="role"
      [ngClass]="{
        'high-emphasis': emphasis === 'High',
        'high-emphasis-disabled':
          emphasis === 'High' && (disabled || (disableWhenSubmit && loading)),
        'medium-emphasis': emphasis === 'Medium',
        'medium-emphasis-disabled':
          emphasis === 'Medium' && (disabled || (disableWhenSubmit && loading)),
        'low-emphasis': emphasis === 'Low',
        'low-emphasis-disabled':
          emphasis === 'Low' && (disabled || (disableWhenSubmit && loading))
      }"
      [disabled]="disabled || (disableWhenSubmit && loading)"
      [attr.aria-label]="arialabel | translation: translationRequest"
      [type]="type"
    >
      <div class="tw-flex tw-items-center">
        <mat-icon class="icon" *ngIf="icon && !loading">{{ icon }}</mat-icon>
        <div
          *ngIf="loading && showSpinner"
          class="tw-mr-2"
          [ngClass]="{
            'app-spinner-white': emphasis !== 'Low',
            'app-spinner-grey': emphasis === 'Low'
          }"
        ></div>
        <div><ng-content></ng-content></div>
      </div>
    </button>
  `,
  styles: [
    `
      .custom-button {
        min-width: 120px !important;
        height: 40px !important;
        font-size: 16px !important;
        line-height: 35px !important;
      }
      .text {
        font-weight: bold;
        letter-spacing: 0px;
        /* border-radius: 8px;*/
      }

      .icon {
        height: 18px;
        width: 18px;
        font-size: 18px;
        margin-right: 4px;
        margin-top: 1px;
      }

      .high-emphasis {
        background-color: var(--sed-dynamic-black-color) !important;
        color: var(--sed-warm-white-color) !important;
        border-radius: 20px;
        padding: 0 20px;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          background-color: var(--sed-sedgwick-green-color) !important;
          color: var(--sed-dynamic-black-color) !important;
          border: 1px solid var(--sed-dynamic-black-color);
        }
      }

      .high-emphasis-disabled {
        background-color: var(--sed-grey-40-color) !important;
        color: var(--sed-black-80-color) !important;
        border: 1px solid var(--sed-black-60-color) !important;
        cursor: not-allowed !important;
      }
      // Low Emphasis Button
      .low-emphasis {
        background-color: var(--sed-warm-white-color) !important;
        color: var(--sed-dynamic-black-color) !important;
        border: 1px solid var(--sed-dynamic-black-color) !important;
        border-radius: 20px;
        padding: 0 20px;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          background-color: var(--sed-sedgwick-green-color) !important;
          color: var(--sed-dynamic-black-color) !important;
        }
      }

      .low-emphasis:focus {
        color: black;
        outline: 2px solid #67707a !important;
      }

      .low-emphasis-disabled {
        background-color: white;
        color: #888888;
        border: 0px solid #dcdcdc;
      }

      .medium-emphasis {
        background-color: var(--color-slate);
        color: white;
      }

      .medium-emphasis-disabled {
        background-color: #dcdcdc;
        color: #888888;
      }

      .medium-emphasis:hover {
        background-color: var(--color-slate-500);
        color: white;
      }

      .text:focus {
        outline: 2px solid black;
      }
    `,
  ],
})
export class ButtonComponent {
  @Input()
  emphasis: Emphasis = 'Low';

  @Input()
  text: string;

  @Input()
  icon: string;

  @Input()
  type: InputType = 'button';

  @Input()
  disabled = false;

  @Input()
  disableWhenSubmit = false;

  @Input()
  loading = false;

  @Input()
  arialabel: string;

  @Input()
  role: string = 'button';

  @Input()
  id: string;

  @Input()
  showSpinner: boolean = true;

  @Input()
  translationRequest: TranslationRequest;

  /**
   * Setting pointer-events is needed to prevent click events from
   * firing when the button is disabled or loading.
   */
  @HostBinding('style.pointer-events') get pEvents(): string {
    if (this.disabled || this.loading) {
      return 'none';
    }
    return 'auto';
  }
}

export type InputType = 'button' | 'submit';
